import React from 'react';
import { t, Trans } from '@lingui/macro';
import styled from 'styled-components';

import { Box, Button, Flex, Heading, Image, Link, Paragraph } from '@rover/kibble/core';
import MQ from '@rover/kibble/styles/MediaQuery';
import useTheme from '@rover/kibble/themes/useTheme';
import { getSourceDescription } from '@rover/rsdk/src/modules/Network/userAgent';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';

import { RoverProtectEvents } from '../analytics';
import { getRoverCard, getSearchCard, widthArray } from '../RoverProtect.utils';

export type Props = {
  personOpk?: string;
  cookieId?: string;
  language?: string;
  backgroundCheckUrl?: string;
  roverGuaranteeUrl?: string;
  veterinaryCareLimit?: string;
};

export type StyledBoxProps = {
  children?: JSX.Element;
};

const StyledBox = ({ children, ...props }: StyledBoxProps): JSX.Element => (
  <Box
    background="primary"
    p={['6x', '8x']}
    borderRadius="primary"
    border="2x solid"
    maxWidth="546px"
    sx={{ boxShadow: 'border_primary' }}
    {...props}
  >
    {children}
  </Box>
);

const StyledRoverCard = styled(Image)`
  position: relative;
  padding: 0px;
  right: -40px;

  ${MQ.XS.toString()} {
    justify-content: center;
    right: 0px;
    margin-right: ${({ theme }) => theme.space['0x']};
    margin-left: ${({ theme }) => theme.space['0x']};
  }
`;

const FourPillarsSection = ({
  personOpk,
  cookieId,
  language,
  backgroundCheckUrl,
  roverGuaranteeUrl,
  veterinaryCareLimit,
}: Props): JSX.Element => {
  const { radii } = useTheme();
  const platform = getSourceDescription();

  return (
    <Flex background="accent_secondary" pt="12x" pb="8x" justifyContent="center">
      <Flex flexDirection="column" mx={['4x', '0x']} width={widthArray}>
        <Heading size="400" mb="12x" mt="1x" textAlign="center">
          {t`Every booking has the tools to help keep you and your pet safe.`}
        </Heading>

        <Flex flexWrap="wrap" gap="12x" mb={['0x', '4x']} justifyContent="center">
          <StyledBox>
            <>
              <Heading size="400" textColor="primary" textAlign="left">
                <Trans>Screened pet sitters</Trans>
              </Heading>
              <Paragraph>
                <Trans>
                  Find pet sitters and dog walkers who have already passed a{' '}
                  <Link href={backgroundCheckUrl || '/background-checks/'} underline={false}>
                    <strong>third-party background check</strong>
                  </Link>
                  . See how many repeat clients a sitter has and read verified reviews from pet
                  parents, like you, to choose someone who’s a great match for you and your pets.
                </Trans>
                <Flex flexDirection={['column', 'row']} mt="1x" position={['static', 'relative']}>
                  <Box
                    background="primary"
                    minWidth={['300px', '300px']}
                    sx={{ boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)' }}
                    p="5x"
                    borderRadius={radii.primary}
                    position={['static', 'absolute']}
                    mt={['4x', '0x']}
                    mb={['4x', '0x']}
                    left="-45px"
                    bottom="-60px"
                  >
                    <Image
                      src={getSearchCard(language)}
                      description="Sitter card image"
                      height="auto"
                      width="100%"
                    />
                  </Box>
                  <Box flex="2" />
                  <Box flex="1">
                    <Trans>
                      Sitters in our community have received over{' '}
                      <strong>7.15 million reviews</strong> - 97% of which have a 5-star rating.
                    </Trans>
                  </Box>
                </Flex>
              </Paragraph>
            </>
          </StyledBox>

          <StyledBox>
            <>
              <Heading size="400" textAlign="left" textColor="primary">
                <Trans>The Rover Guarantee</Trans>
              </Heading>
              <Paragraph>
                <Trans>
                  If your pet needs <strong>vet care</strong> during a booking,{' '}
                  <Link
                    href={roverGuaranteeUrl || 'rover-guarantee'}
                    underline={false}
                    sx={{ verticalAlign: 'center' }}
                  >
                    <strong>the Rover Guarantee</strong>
                  </Link>{' '}
                  can help them get it-fast—with <strong>up to {veterinaryCareLimit}</strong> in
                  reimbursement for eligible care. Every booking made on the platform is backed by
                  the Rover Guarantee.
                </Trans>
              </Paragraph>
            </>
          </StyledBox>

          <StyledBox>
            <>
              {' '}
              <Heading size="400" textAlign="left" textColor="primary">
                <Trans>24/7 support</Trans>
              </Heading>
              <Paragraph>
                <Trans>
                  Our support team and{' '}
                  <Link
                    href="/blog/safety"
                    underline={false}
                    target="_blank"
                    sx={{ verticalAlign: 'center' }}
                  >
                    <strong>safety resources</strong>
                  </Link>{' '}
                  are available <strong>24 hours a day, 7 days a week</strong>. We love animals and
                  we know how important it is to be able to <strong>talk to someone</strong> in the
                  event of a problem. During every booking, your sitter also has access to advice
                  from qualified veterinary professionals.
                </Trans>
              </Paragraph>
            </>
          </StyledBox>

          <StyledBox>
            <Flex flexDirection={['column', 'row']}>
              <Flex flexDirection="column">
                {' '}
                <Heading size="400" textAlign="left" textColor="primary">
                  <Trans>Messaging & photos</Trans>
                </Heading>
                <Paragraph>
                  <Trans>
                    Never wonder how it's going—use your app inbox to get adorable photo updates and
                    stay in touch with your sitter. Sitters can also send a report card detailing
                    when they arrived and left, a map of any walks, and photos for certain services.
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    <strong>Fun fact: </strong>Sitters and dogs on Rover have walked over 18.6
                    million miles together—that’s about 747 times around the globe!
                  </Trans>
                </Paragraph>
              </Flex>
              <Flex height="320px" justifyContent="center">
                <StyledRoverCard
                  src={getRoverCard(language)}
                  description="Rover card image"
                  height="100%"
                  width="auto"
                />
              </Flex>
            </Flex>
          </StyledBox>
        </Flex>

        <Paragraph
          webProps={{ maxWidth: 'none' }}
          size="200"
          textAlign="center"
          marginTop={['12x', '8x']}
          textColor="primary"
        >
          <Heading size="400" textAlign="center" textColor="primary" mb="6x">
            <Trans>Discover trusted pet sitters in your neighborhood.</Trans>
          </Heading>
          <Button
            variant="primary"
            href="/search"
            size="large"
            onPress={() => {
              fireDataLayerEvent(
                RoverProtectEvents.SearchClick({
                  source: platform,
                  personId: personOpk,
                  cookieId,
                })
              );
            }}
          >
            <Trans>Book your perfect sitter</Trans>
          </Button>
        </Paragraph>
      </Flex>
    </Flex>
  );
};

export default FourPillarsSection;
